/**
 * @fileoverview One plant as a line of text with a thumbnail.
 *
 * The densest of the three views. It answers the questions a list is good
 * at — what have I got, where is it, how is its week, what stage is it at —
 * with the plant itself one tap away.
 *
 * The name is the backend's one name (lib/plantName, services/grow/
 * plantNaming: "Devil's Ear Lettuce"), with the day it was added in bold when
 * another plant of the same name is in the same bed (NameParts). The bed sits
 * right under it, as on the card; it used to be at the far end of the row,
 * and on a phone not shown at all. The week's badge (its state and icons,
 * see PlantMarks) sits in the top right corner, so the states run down the
 * right-hand edge of the list. The name wraps round it rather than beside
 * it: in a column beside the badge, "Vining Cherry Tomato" was cut to
 * "Vining Cherry…".
 *
 * Where: PlantGrid's list view.
 * API: none of its own; the week's badge reads the plants brief (usePlantsBrief).
 * Settings: none.
 * Words (en/es): plants.tile.open, plants.tile.restore, plants.archive.delete.open,
 * plants.form.deleteForeverEllipsis, plants.card.state.*.
 *
 * @module PlantRow
 */

import React from 'react';
import { MapPin, Trash2, Undo2 } from 'lucide-react';
import { Plant } from '@/shared/types/plant';
import { StageId } from '@/shared/types/stage';
import { getStageTheme } from '@/shared/theme/stageThemes';
import { plantCoverUrl } from '../lib/plantPhotoUrl';
import { plantSubtitle } from '../lib/plantSubtitle';
import { brandConfig } from '@/shared/styles/base';
import { cn } from '@/lib/utils';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { Pill } from '@/shared/components/ui/Pill';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { usePlantsBrief } from '@/shared/hooks/usePlantsBrief';
import { PlantMarks } from './card/PlantMarks';
import { NameParts } from '@/shared/components/NameParts';
import { plantShown, plantTitle } from '@/shared/lib/plantName';

/** The plant a row shows, and what its buttons do. */
interface PlantRowProps {
  /** The plant. */
  plant: Plant;
  /** Opens the plant full size. The thumbnail and the names do this. */
  onOpen: (plant: Plant) => void;
  /** The date the current ordering is based on, where it is a date. */
  sortedOn?: string | null;
  /** True when this plant has been taken out of the garden. */
  archived?: boolean;
  /** Puts it back. Offered on an archived plant, which a card cannot restore. */
  onRestore?: (plant: Plant) => void;
  /** True while a restore is on its way, so it cannot be sent twice. */
  isRestoring?: boolean;
  /** Asks to delete it for good (the caller opens the type-DELETE confirm). Offered on an archived plant. */
  onDelete?: (plant: Plant) => void;
}

/**
 * One plant as a row: thumbnail, the week's badge top right, name, type, bed and stage.
 *
 * @param props - PlantRowProps.
 */
export const PlantRow: React.FC<PlantRowProps> = ({
  plant, onOpen, sortedOn, archived, onRestore, isRestoring, onDelete,
}) => {
  const { reactTranslate: t } = useTranslation();
  /** The thumbnail: the plant's cover picture, small. */
  const cover = plantCoverUrl(plant, 'thumb');
  /** Its stage now, for the badge: the server's (null: none recorded). */
  const stage = plant.stage;
  /** The sort's date under the name, when the garden is sorted by one. */
  const subtitle = plantSubtitle(plant, sortedOn);
  /** The name as the backend says it (lib/plantName), with the day added in bold when a twin shares its bed. */
  // The bed is on its own line under the name: not said again in it.
  const title = plantTitle(plant, { bedShown: true });
  /** The name as text, for the row's labels. */
  const shown = plantShown(plant);
  /** The stage's colours. */
  const theme = getStageTheme(stage?.id as StageId);
  const { forPlant } = usePlantsBrief();
  // What the week holds, as a badge, so the list reads at a glance. None for
  // a sheltered bed, which the weather does not reach, or a plant out of the garden.
  const week = !plant.location.sheltered && !archived ? forPlant(plant.plant_obj_id)?.week ?? null : null;
  // The badge is hidden from screen readers, so its state is said here.
  const openLabel = t('plants.tile.open', { name: shown })
    + (week ? `, ${t(`plants.card.state.${week.state}`)}` : '');

  return (
    <div className={cn(
      'flex items-center gap-3 px-3 py-2 transition-colors hover:bg-accent/40',
      archived && 'opacity-70'
    )}>
      {/* Not a link: this goes nowhere, it opens the plant you are already
          looking at, larger. Restore, on an archived row, is the one other
          thing a row does. */}
      <button
        type="button"
        onClick={() => onOpen(plant)}
        aria-label={openLabel}
        className="flex min-w-0 flex-1 items-center gap-3 text-left"
      >
        <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-sm">
          {cover ? (
            <FramedPhoto src={cover} alt="" loading="lazy" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-muted">
              <brandConfig.emptyStateIcon className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
        </div>

        {/* The name keeps two lines, because on a phone a one-line name
            came out as "Cherry Swee...", which identifies nothing. */}
        <div className="min-w-0 flex-1">
          {/* Top right, floated so the name's first line wraps round it and
              the rest of the name has the full width. No line clamp on the
              name: a clamped block sits beside a float instead of round it.
              The negative margin keeps the float to one line of the name;
              the badge is a pixel taller, which squeezed the second line too. */}
          <PlantMarks week={week} className="float-right -mb-1 ml-2" />
          <p className="break-words text-sm font-medium leading-snug"><NameParts parts={title} /></p>
          {subtitle && <p className="truncate text-xs text-muted-foreground">{subtitle}</p>}
          {/* Where it is, right under what it is. */}
          <p className="mt-0.5 flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{plant.location.name}</span>
          </p>
          {stage && <Pill size="sm" theme={theme} className="mt-1">{stage.name}</Pill>}
        </div>
      </button>

      {/* Restore only — see PlantTile for why the pencil left both of these. */}
      {archived && onRestore && (
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => onRestore(plant)}
            disabled={isRestoring}
            aria-label={t('plants.tile.restore', { name: shown })}
            className="rounded-sm p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
          >
            <Undo2 className="h-4 w-4" aria-hidden="true" />
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(plant)}
              disabled={isRestoring}
              aria-label={t('plants.archive.delete.open', { name: shown })}
              title={t('plants.form.deleteForeverEllipsis')}
              className="rounded-sm p-1 text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};
